// Pure helpers for expandable projected-payment ledger (Official legs + formula steps).

/** Official ddInputCostType values — same set as margincoverageoption.com. */
export const MCO_INPUT_LEGS = [
  { type: 1, name: "Crop" },
  { type: 2, name: "DAP" },
  { type: 3, name: "Natural Gas" },
  { type: 4, name: "Diesel" },
  { type: 5, name: "Urea" },
  { type: 7, name: "Potash" },
];

function pick(row, ...keys) {
  if (!row) return undefined;
  const lower = {};
  for (const [k, v] of Object.entries(row)) {
    lower[String(k).toLowerCase().replaceAll(" ", "_")] = v;
  }
  for (const k of keys) {
    const v = lower[String(k).toLowerCase().replaceAll(" ", "_")];
    if (v != null && v !== "") return v;
  }
  return undefined;
}

function numOrNull(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** One Official PriceDiscovery row → compact leg line for the ledger table. */
export function summarizePriceDiscoveryLeg(pd, legName) {
  if (!pd) {
    return {
      leg: legName,
      symbol: "",
      projectedPrice: null,
      harvestPrice: null,
      volatility: null,
      projectedWindow: null,
      harvestWindow: null,
    };
  }
  const summary = pick(pd, "mcopricediscoverysummary", "mcoPriceDiscoverySummary") || {};
  const futures = pick(pd, "mcofuturescontractdetails", "mcoFuturesContractDetails") || {};
  const pStart = pick(summary, "projectedStartDate", "projectedstartdate");
  const pEnd = pick(summary, "projectedEndDate", "projectedenddate");
  const hStart = pick(summary, "harvestStartDate", "harveststartdate");
  const hEnd = pick(summary, "harvestEndDate", "harvestenddate");
  return {
    leg: legName,
    symbol: String(pick(futures, "symbol") ?? ""),
    projectedPrice: numOrNull(
      pick(summary, "projectedPrice", "projectedprice") ?? pick(pd, "projectedprice", "projectedPrice")
    ),
    harvestPrice: numOrNull(pick(summary, "harvestPrice", "harvestprice") ?? pick(pd, "harvestprice", "harvestPrice")),
    volatility: numOrNull(pick(summary, "volatility")),
    projectedWindow: pStart || pEnd ? [pStart, pEnd] : null,
    harvestWindow: hStart || hEnd ? [hStart, hEnd] : null,
  };
}

/**
 * Ledger-style formula steps for the projected payment card.
 * @param {object} input — same fields fed to computeMcoProjectedPayment
 * @param {object|null} result — proj.result when ready
 */
export function buildPaymentFormulaRows(input, result) {
  const rows = [
    { label: "Expected area yield", value: input.expectedYield, unit: "/ac" },
    { label: "Final / stress yield", value: input.finalYield, unit: "/ac" },
    { label: "Approved yield (APH)", value: input.approvedYield, unit: "/ac" },
    { label: "Projected crop price", value: input.projectedPrice, unit: "" },
    { label: "Harvest crop price", value: input.harvestPrice, unit: "" },
    {
      label: "Expected revenue",
      value: input.expectedRevenue,
      unit: "$/ac",
    },
    {
      label: "Expected margin",
      value: input.expectedMargin,
      unit: "$/ac",
    },
    { label: "Projected costs", value: input.projectedCosts, unit: "$/ac" },
    {
      label: "Harvest costs",
      value: input.harvestCosts,
      unit: "$/ac",
      note: input.harvestCostsHeldAtExpected ? "held at expected costs (harvest cost not published yet)" : null,
    },
    {
      label: "Coverage band",
      value: null,
      text: `${input.coverageStart} → ${input.areaLossEnd}`,
    },
  ];
  if (result) {
    rows.push(
      { label: "Coverage range", value: result.coverageRange, unit: "" },
      { label: "Coverage value (ER×range)", value: result.coverageValue, unit: "$/ac" },
      { label: "Trigger margin", value: result.triggerMargin, unit: "$/ac" },
      { label: "Harvest revenue", value: result.harvestRevenue, unit: "$/ac" },
      { label: "Harvest margin", value: result.harvestMargin, unit: "$/ac" },
      { label: "Deficient (TM−HM)", value: result.deficient, unit: "$/ac" },
      { label: "Payment factor", value: result.paymentFactor, unit: "" },
      {
        label: "Protection $/ac",
        value: result.protectionPerAcre,
        unit: "$/ac",
      },
      { label: "Projected MCO payment", value: result.mcoPayment, unit: "$/ac" }
    );
  }
  return rows;
}

/** Compact actuarial lines from flattened Official Actuarial row. */
export function buildActuarialLedgerRows(act) {
  if (!act) return [];
  const keys = [
    ["expectedcosts", "Expected costs"],
    ["expectedrevenueamount", "Expected revenue"],
    ["expectedmarginamount", "Expected margin"],
    ["triggermargin", "Trigger margin"],
    ["producerpremium", "Producer premium (est.)"],
  ];
  const lower = {};
  for (const [k, v] of Object.entries(act)) {
    lower[String(k).toLowerCase().replaceAll(" ", "_")] = v;
  }
  return keys
    .filter(([k]) => lower[k] != null && lower[k] !== "")
    .map(([k, label]) => ({ label, value: Number(lower[k]) }));
}
